import React from "react";
import Modal from "../common/Modal";
import type { ILeaveBalanceReportItem } from "../../types/leave.api.types";
import { getUtilization } from "../../utils/leaveUtils";
import { getAvatarGradient } from "../../utils/avatarUtils";

export interface LeaveBalanceDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  record: ILeaveBalanceReportItem | null;
  onExport?: () => void;
  exporting?: boolean;
}

function getInitials(name?: string): string {
  if (!name) return "E";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

const LeaveBalanceDetailModal: React.FC<LeaveBalanceDetailModalProps> = ({
  isOpen,
  onClose,
  record,
}) => {
  if (!record) return <Modal isOpen={false} onClose={onClose} />;

  const leaveBalances = record.leaveBalances ?? [];
  const avatarBg = getAvatarGradient(record.fullName || "");

  const customHeader = (
    <div className="flex items-center gap-3 w-full">
      <div
        className="w-10 h-10 rounded-md flex items-center justify-center text-white font-bold text-label shrink-0 shadow-sm"
        style={{ background: getAvatarGradient(record.fullName || "") }}
      >
        {getInitials(record.fullName)}
      </div>
      <div className="min-w-0 flex-1 flex flex-col justify-center">
        <h2 className="text-base font-extrabold text-[var(--color-text-primary)] leading-tight truncate m-0">
          {record.fullName}
        </h2>
        <p className="text-body-sm font-normal text-[var(--color-text-tertiary)] mt-0.5 truncate m-0">
          {record.departmentName || "Engineering"} · Full leave breakdown
        </p>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={customHeader}
      size="lg"
      className="sm:max-w-lg rounded-3xl overflow-hidden shadow-2xl border-0"
      headerClassName="px-7 pt-5 pb-3 border-b-0"
      bodyClassName="px-7 pb-6 pt-1 max-h-96 overflow-y-auto scrollbar-thin"
    >
      <div className="space-y-4">
        {leaveBalances.map((b) => {
          const val = b.value ?? 0;
          const { total, percent, bg } = getUtilization(val, b.label);

          return (
            <div key={b.label} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-label font-bold text-[var(--color-text-primary)]">{b.label}</span>
                <span className="text-body-sm font-semibold text-[var(--color-text-secondary)]">
                  {total > 0 ? `${val} of ${total} left` : `${val} days`}
                </span>
              </div>
              <div className="w-full bg-[var(--color-bg-track)] shadow-inner rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${bg}`}
                  style={{
                    width: `${percent}%`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default LeaveBalanceDetailModal;